/**
 * Phase 22 Plan 22-03 — Shared zod schema for Client create/edit forms.
 *
 * D-225 validation: Name 3-200, Code 2-30 regex, email RFC-ish, phone digits.
 * Exported separately from ClientFormFields so it can be imported by
 * CreateClientDialog and the pages without circular dependencies.
 */

import { z } from 'zod';

export const clientFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(200, 'Name must be 200 characters or fewer'),
  code: z
    .string()
    .min(2, 'Code must be at least 2 characters')
    .max(30, 'Code must be 30 characters or fewer')
    .regex(/^[A-Za-z0-9_-]+$/, 'Code must contain only letters, numbers, underscores, or dashes'),
  contactName: z
    .string()
    .max(200, 'Max 200 characters')
    .optional()
    .or(z.literal('')),
  contactEmail: z
    .string()
    .max(254, 'Max 254 characters')
    .regex(
      /^$|^[^@\s]+@[^@\s]+\.[^@\s]+$/,
      'Enter a valid email address',
    )
    .optional()
    .or(z.literal('')),
  contactPhone: z
    .string()
    .max(20, 'Max 20 characters')
    .regex(/^$|^\d{5,15}$/, 'Phone must be 5-15 digits only')
    .optional()
    .or(z.literal('')),
  address: z
    .string()
    .max(500, 'Max 500 characters')
    .optional()
    .or(z.literal('')),
  gstNumber: z
    .string()
    .max(30, 'Max 30 characters')
    .optional()
    .or(z.literal('')),
  panNumber: z
    .string()
    .max(20, 'Max 20 characters')
    .optional()
    .or(z.literal('')),
  notes: z
    .string()
    .max(2000, 'Max 2000 characters')
    .optional()
    .or(z.literal('')),
});

export type ClientFormValues = z.infer<typeof clientFormSchema>;
